import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import BoardLoopsIndexTableRowComponent from 'webapp/components/board-loops/index/table-row';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonRecentlyDeletedInfoComponent from 'webapp/components/common/recently-deleted-info';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import SkeletonScreenComponent from 'webapp/components/skeleton-screen';
import TableSorterComponent from 'webapp/components/table-sorter';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type {
	AddTagAction,
	DeleteAction,
	DeletePermanentlyAction,
	RestoreAction,
	ToggleFavoriteAction,
} from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import type BoardLoop from 'webapp/models-custom/board-loop';
import FilterSortBoardLoop, {
	BoardLoopCategory,
	BoardLoopFilterId,
} from 'webapp/models-custom/filter-sort/board-loop';

export interface Args {
	model: BoardLoop[];
	onCreate: () => void;
	onOpenPreview: (model: BoardLoop, boardLoops: BoardLoop[]) => void;
	onEdit: (model: BoardLoop) => void;
	onToggleFavorite: (model: BoardLoop) => void;
	onShare: (model: BoardLoop) => void;
	onOpenFullscreenView: (model: BoardLoop) => void;
	onOpenInDesigner: (model: BoardLoop) => void;
	onDuplicate: (model: BoardLoop) => void;
	onCreateReport: (model: BoardLoop) => void;
	onDelete: (model: BoardLoop) => void;
	onSaveAsTemplate: (model: BoardLoop) => void;
	onRestore: (model: BoardLoop) => void;
	onDeletePermanently: (model: BoardLoop) => void;
	updateIsRecentlyDeletedFilterActive: (isActive: boolean) => void;
}

type Action =
	ToggleFavoriteAction | AddTagAction | DeleteAction | DeletePermanentlyAction | RestoreAction;

export default class BoardLoopsIndexTableComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	filterSort: FilterSortBoardLoop;
	batchSelect: BatchSelect<BoardLoop>;
	addButtonText = 'New loop';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortBoardLoop({
			getAllItems: () => {
				return this.args.model;
			},
			afterFilter: () => {
				this.args.updateIsRecentlyDeletedFilterActive(filterSort.isRecentlyDeletedSelected);
			},
		});

		this.filterSort = filterSort;

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as Action);
			},
		});

		void this.mmNotificationsService.requestNotificationsForFeature('loop');
	}

	get addBtnShown(): boolean {
		return !this.isRecentlyDeletedActive;
	}

	get addButtonContainerNode() {
		return isAlive(this) && !this.selectedItems.length && this.addBtnShown
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): BoardLoop[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<Action>[] {
		const batchSelect = this.batchSelect;

		if (this.filterSort.isRecentlyDeletedSelected) {
			return [batchSelect.deletePermanentlyOption, batchSelect.restoreOption];
		}

		return [
			batchSelect.toggleFavoriteOption,
			batchSelect.addTagsOption,
			batchSelect.deleteOption,
		];
	}

	get isRecentlyDeletedActive() {
		return this.filterSort.isRecentlyDeletedSelected;
	}

	get isRecentlyDeletedActiveWithResults() {
		return this.filterSort.isRecentlyDeletedSelected && this.filterSort.results.length > 0;
	}

	get isRecentlyDeletedLoading(): boolean {
		return (
			this.filterSort.results.length === 0 &&
			this.mmBoardLoopsService.isRecentlyDeletedLoading
		);
	}

	doBatchAction(actionName: Action) {
		return this[actionName]();
	}

	@action
	batchDelete() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;
		const remainingItemsAfterDelete =
			this.filterSort.results.length - this.selectedItems.length;

		return this.mmBoardLoopsService.batchDelete(this.selectedItems).then((result) => {
			if (isRecentlyDeletedActive && remainingItemsAfterDelete === 0) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	batchToggleFavorite() {
		if (this.batchSelect.isEverythingFavorited) {
			return this.mmBoardLoopsService.batchUnfavorite(this.selectedItems);
		} else {
			return this.mmBoardLoopsService.batchFavorite(this.selectedItems);
		}
	}

	@action
	batchUpdateTags() {
		return this.mmBoardLoopsService.batchUpdateTags(this.selectedItems);
	}

	@action
	batchRestore() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;
		const remainingItemsAfterRestore =
			this.filterSort.results.length - this.selectedItems.length;

		return this.mmBoardLoopsService.batchRestore(this.selectedItems).then((result) => {
			if (isRecentlyDeletedActive && remainingItemsAfterRestore === 0) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	batchDeletePermanently() {
		return this.batchDelete();
	}

	@action
	deleteAllPermanently() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;

		return this.mmBoardLoopsService.batchDelete(this.filterSort.results).then((result) => {
			if (isRecentlyDeletedActive) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	createNew() {
		this.args.onCreate();
	}

	@action
	openPreview(model: BoardLoop) {
		this.args.onOpenPreview(model, this.filterSort?.results || []);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}

	resetRecentlyDeletedFilter(): void {
		const categoryFilter = this.filterSort.findFilter(BoardLoopFilterId.CATEGORY);
		if (categoryFilter) {
			categoryFilter.selectOption(BoardLoopCategory.ALL);
		}
	}

	@action
	handleSingleItemPermanentDelete(): void {
		if (this.isRecentlyDeletedActive && this.filterSort.results.length === 0) {
			this.resetRecentlyDeletedFilter();
		}
	}

	@action
	handleSingleItemRestore(): void {
		if (this.isRecentlyDeletedActive && this.filterSort.results.length === 1) {
			this.resetRecentlyDeletedFilter();
		}
	}
	<template>
		<div class="bottom-padding">

			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} @entityName="looped dashboards" />

			{{#if this.isRecentlyDeletedActiveWithResults}}
				<CommonRecentlyDeletedInfoComponent
					@onDeleteAllPermanently={{this.deleteAllPermanently}}
				/>
			{{/if}}

			{{#if this.isRecentlyDeletedLoading}}
				<SkeletonScreenComponent @type="table" />
			{{/if}}

			<table
				id="board-loops-list"
				class={{joinClasses
					(array
						"flexible-table"
						"bigtable"
						"has-checkbox"
						"has-avatar-big"
						(if this.filterSort.noResults "hide")
					)
				}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange this.filterSort.results}}
			>
				<colgroup>
					<col class="cc" />
					<col class="h0" />
					<col width="20%" />
					<col width="180" />
					<col width="180" />
					<col width="15%" />
					<col width="15%" />
					<col class="button-ct" />
				</colgroup>
				<thead>
					<tr>
						<CommonBatchTableHeaderComponent @batchSelect={{this.batchSelect}} />
						<th class="h0">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="title"
								@name="Name"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th></th>
						<th class="prio-medium">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="boardCount"
								@name="# of dashboards"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="boardSourceCount"
								@name="Data sources included"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-medium">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="ownerId"
								@name="Owner"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="tagsCount"
								@name="Tags"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.filterSort.results as |item|}}
						<BoardLoopsIndexTableRowComponent
							@model={{item}}
							@batchSelect={{this.batchSelect}}
							@onOpenPreview={{this.openPreview}}
							@onEdit={{@onEdit}}
							@onToggleFavorite={{@onToggleFavorite}}
							@onShare={{@onShare}}
							@onOpenFullscreenView={{@onOpenFullscreenView}}
							@onOpenInDesigner={{@onOpenInDesigner}}
							@onDuplicate={{@onDuplicate}}
							@onCreateReport={{@onCreateReport}}
							@onDelete={{@onDelete}}
							@onSaveAsTemplate={{@onSaveAsTemplate}}
							@onRestore={{@onRestore}}
							@onDeletePermanently={{@onDeletePermanently}}
							@onSingleItemRestore={{this.handleSingleItemRestore}}
							@onSingleItemPermanentDelete={{this.handleSingleItemPermanentDelete}}
						/>
					{{/each}}
				</tbody>
			</table>
			{{#if this.addBtnShown}}
				<TableAddNewItemComponent
					@title={{this.addButtonText}}
					@addNewItem={{@onCreate}}
					@model={{this.filterSort}}
				/>
			{{/if}}
		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{this.createNew}}
				/>
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
